import { useNavigate, useParams } from 'react-router-dom';
import { Advertiser, Brand, Line } from 'components/common/types/Deal.types';
import {
  DirectSales,
  DirectSalesCampaignDealLineResponse,
  DirectSalesCampaignDefinitionExclusions,
  DirectSalesLine,
  DirectSalesMediaType,
  DirectSalesStatusOption,
  DirectSalesTransientStates,
} from 'components/common/types/DirectSalesCampaign.types';
import { CodeNameModel } from 'components/common/types';
import {
  addDirectSalesLineId,
  changeActiveDirectSalesLine,
  changeDealCurrentLineData,
  changeDealData,
  changeDirectSalesData,
  changeDirectSalesNegotiationFailedValues,
  changeFormParams,
  changeTransientState,
  clearCampaignPlanShareUrl,
  clearDirectSalesAssetList,
  clearDirectSalesPrices,
  clearLineAssets,
  clearTransientLineStates,
  hidePlannerSecondaryPanel,
  resetCampaignLevelChanges,
  setCampaignViolations,
  setDirectSalesStates,
  setIsFetchingAdvancedAvailabilityReport,
  updateBackupFormDataCommonDeal,
  updateBackupFormDataLine,
  updateCurrentLineAndBackupLineProperty,
  updateDirectSalesSessionLock,
} from 'store/dealManagement/reducer';
import { addToastNotification, addNotifications } from 'store/notification/reducer';
import {
  acceptOrDiscardChanges,
  createDirectSalesCampaign,
  createDirectSalesLine,
  editDirectSalesCampaign,
  pollDealSalesCampaign,
} from 'modules/api/directSalesCampaign';
import { CAMPAIGN_VIOLATION_ERROR_HEADER } from 'consts/campaignViolations';
import {
  CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_DESCRIPTION,
  CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_MESSAGE,
} from 'components/common/Modals/ShareCampaignPlanModal/ShareCampaignPlanModal.types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import {
  getEveryLineStatus,
  getIsDirectSalesSessionReadOnly,
  isCampaignStatusPriced,
} from 'store/dealManagement/selectors';
import { setLoadedDirectSalesDataWithPublisherDefaults } from 'store/dealManagement/actions/setLoadedDirectSalesData';
import { getDefaultDirectSalesPricingModeForMediaType, getMarketId } from 'store/publisher/selectors';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getCampaignViolations } from 'utils/getCampaignViolations';
import { CampaignVersioningEditingStatus, CampaignVersioningErrors } from 'consts/directSalesCampaignVersioning';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { hideBanner, showBanner } from 'store/banner/reducer';
import { DirectSalesCampaignResponse } from 'components/common/types/DirectSalesCampaignResponse.types';
import usePrevious from 'customHooks/usePrevious';
import { ToasterTimeout, ToasterType } from 'lib/Toaster/Toaster.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import {
  getAdvertiser,
  getAgency,
  getBrand,
  getProductCategory,
  getSpecialist,
} from 'store/dealManagement/selectors/getCommonDealDetails';
import { getDirectSalesCampaignStatus } from 'store/dealManagement/selectors/getDirectSalesCampaignState';
import { getHasDirectSalesCampaignPlanLinkInvalidatingChange } from 'store/dealManagement/selectors/getHasDirectSalesCampaignLevelChanged';
import { mergeCurrentLineToAllLines } from 'components/pages/Planner/hooks/utils/mergeCurrentLineToAllLines';
import { PolygonRequest } from 'components/common/types/Polygon.types';
import { addLinePolygon, deleteLinePolygon, getPolygons } from 'modules/api/polygon';
import { loadPolygons, removeLassoSelection } from 'store/carto/reducer';
import { getDirectSalesStatus } from 'store/dealManagement/selectors/getDirectSalesStatus';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import useRetrieveDirectSalesCampaign from './useRetrieveDirectSalesCampaign';
import { isDirectSalesCampaignCloned } from '../utils/isDirectSalesCampaignCloned';
import buildLinesStatesWithAllocation from '../utils/buildLinesStatesWithAllocation';
import { useDirectSalesDraftErrorHandler } from '../useDirectSalesDraftErrorHandler';
import { transformFailedLinesToConfirmToasts } from '../utils/transformLinesAllocationFailuresToToasts';
import { getCampaignVersioningMessageExtended } from '../utils/getCampaignVersioningMessagesExtended';
import { updateGridNegotiationTypes } from '../utils/updateGridNegotiationTypes';
import {
  isTransientCommitDisabled,
  validateAttemptToConfirmChanges,
  ValidationResult,
} from '../utils/directSalesTransientLineUtils';
import { getDirectSalesFailedLines, isSomeDirectSalesStatusFailed } from '../utils/directSalesFailedStatus';
import { useDirectSalesPricedSolution } from '../useDirectSalesPricedSolution';
import { useCheckDirectSalesMandatoryFields } from '../useCheckDirectSalesMandatoryFields';
import { updateDatesForDirectSalesLines } from '../utils/updateDatesForDirectSalesLines';
import { createNewDirectSalesLine, generateUniqueDealLineName } from '../utils/createNewDirectSalesLine';
import { useMarketDateOffset } from '../useMarketDateOffset';
import {
  transformAllLineData,
  transformDirectSalesDealLineRequest,
  transformDirectSalesWithLineFormRequest,
} from '../utils/transformDirectSalesCampaignRequests';
import { transformDirectSalesBookingResponse } from '../utils/transformDirectSalesCampaignResponses';
import { transformPolygonsResponse } from '../utils/transformPolygonsResponse';

interface UseUpdateDirectSalesCampaign {
  editDeal: (data?: {
    currentLineData?: Partial<Line>;
    directSalesData?: Partial<DirectSales>;
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>;
  }) => Promise<boolean>;
  saveDeal: (
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>,
    callback?: VoidFunction,
  ) => Promise<boolean>;
  saveLinePolygon: (polygonId: string, polygonData: PolygonRequest) => Promise<void>;
  removeLinePolygon: (polygonId: string) => Promise<void>;
  duplicateCampaign: (data?: {
    campaignStartDate: Date;
    areCancelledLinesIncluded: boolean;
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>;
    linesData?: Partial<DirectSalesLine>[];
  }) => Promise<void>;
  confirmChanges: () => void;
  addNewLine: ({ backupFormDataLines }: { backupFormDataLines: DirectSalesLine[] }) => Promise<void>;
  handleTransientBanner: (
    newTransientState: CampaignVersioningEditingStatus | CampaignVersioningErrors | undefined,
  ) => void;
  cancelTransientState: (transientCampaignId: string) => Promise<void>;
  discardChanges: (campaignId: string) => Promise<void>;
}

const useUpdateDirectSalesCampaign = (): UseUpdateDirectSalesCampaign => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getUpdatedDirectSalesCampaign = useRetrieveDirectSalesCampaign();
  const handlePricedSolution = useDirectSalesPricedSolution();
  const isDirectSalesSessionLockedAndReadOnly = useSelector(getIsDirectSalesSessionReadOnly);
  const { handleDraftFail } = useDirectSalesDraftErrorHandler();
  const { campaignId: originalCampaignId } = useParams();
  const campaignStatus = useSelector(getDirectSalesCampaignStatus);
  const { areAllMandatoryFieldsFilled } = useCheckDirectSalesMandatoryFields();
  const startDateOffset = useMarketDateOffset();
  const isLineLevelStatusDisabled = useHasFeatureAccess(FeatureFlags.DISABLED_DIRECT_LINE_STATUS);

  const updatedGridNegotiationTypes = useSelector(
    (state: Store) => state.dealManagement.directSales.updatedGridNegotiationTypes,
  );
  const directSales = useSelector((state: Store) => state.dealManagement.directSales);
  const lines = useSelector(
    (state: Store) => state.dealManagement.backupFormData.lines,
    shallowEqual,
  ) as DirectSalesLine[];
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const defaultNonNetworkPricingModeForNewLine = useSelector((state: Store) =>
    getDefaultDirectSalesPricingModeForMediaType(
      state,
      state.dealManagement.directSales.mediaType as DirectSalesMediaType | undefined,
    ),
  );
  const linesStatuses = useSelector(getEveryLineStatus);
  const isPricedCampaign = useSelector(isCampaignStatusPriced);
  const hasCampaignPlanLinkInvalidatingChange = useSelector(getHasDirectSalesCampaignPlanLinkInvalidatingChange);
  const campaignId = useSelector((state: Store) => state.dealManagement.commonDeal.dealId);
  const isCampaignDrawerOpen = useSelector((state: Store) => state.dealManagement.isCampaignDrawerOpen);
  const isCampaignDrawerOpenRef = useRef(isCampaignDrawerOpen);
  isCampaignDrawerOpenRef.current = isCampaignDrawerOpen;
  const advertiser = useSelector(getAdvertiser);
  const brand = useSelector(getBrand);
  const productCategory = useSelector(getProductCategory);
  const agency = useSelector(getAgency);
  const specialist = useSelector(getSpecialist);
  const salesPerson = useSelector((state: Store) => state.dealManagement.commonDeal.salesPerson);
  const salesTeam = useSelector((state: Store) => state.dealManagement.commonDeal.salesTeam);
  const adminPerson = useSelector((state: Store) => state.dealManagement.commonDeal.adminPerson);
  const dealName = useSelector((state: Store) => state.dealManagement.commonDeal.dealName);
  const currentLine = useSelector(getCurrentLine);
  const dealLineId = useSelector(getCurrentLineId);
  const currentLineStatus = useSelector(getDirectSalesStatus);
  const marketId = useSelector(getMarketId);

  const productCategoryExclusivity = useSelector(
    (state: Store) => state.dealManagement.commonDeal.productCategoryExclusivity,
  );
  const duplicationExclusion = useSelector((state: Store) => state.dealManagement.commonDeal.duplicationExclusion);
  const juxtapositionExclusion = useSelector((state: Store) => state.dealManagement.commonDeal.juxtapositionExclusion);
  const exclusions = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions);
  const advertiserBlockExclusion = useSelector(
    (state: Store) => state.dealManagement.commonDeal.advertiserBlockExclusion,
  );
  const agencyBlockExclusion = useSelector((state: Store) => state.dealManagement.commonDeal.agencyBlockExclusion);
  const specialistBlockExclusion = useSelector(
    (state: Store) => state.dealManagement.commonDeal.specialistBlockExclusion,
  );

  const currentTransientState = useSelector((state: Store) => state.dealManagement.directSales.transientState);
  const transientStateDealLines = useSelector((state: Store) => state.dealManagement.directSales.transientLineStates);

  const previousLines = usePrevious(lines);
  const previousTransientLines = usePrevious(transientStateDealLines);

  const isCampaignCloned = useMemo(() => isDirectSalesCampaignCloned(campaignId), [campaignId]);

  const isCommitDisabled = useMemo(() => {
    return isTransientCommitDisabled({
      areAllMandatoryFieldsFilled,
      transientStateDealLines,
      campaignState: { status: campaignStatus },
      linesStatuses,
      isLineLevelStatusDisabled,
    });
  }, [
    currentLineStatus,
    isEqual(previousLines, lines),
    isEqual(previousTransientLines, transientStateDealLines),
    campaignId,
    dealLineId,
  ]);

  const cancelTransientState = useCallback(
    async (transientCampaignId: string): Promise<void> => {
      await acceptOrDiscardChanges(
        { transientAction: DirectSalesTransientStates.CANCEL },
        marketId,
        transientCampaignId,
        cancelFunctions,
      );

      dispatch(clearTransientLineStates());
      dispatch(hideBanner());
    },
    [marketId, dispatch],
  );

  const discardChanges = useCallback(
    async (providedCampaignId: string): Promise<void> => {
      dispatch(changeFormParams({ isEditingDisabled: true, isLoading: true }));

      try {
        await cancelTransientState(providedCampaignId);

        if (!originalCampaignId) return;

        const {
          campaign: {
            body: { pricingSolution },
          },
          deal: commonDeal,
          lines: updatedLines,
        } = await getUpdatedDirectSalesCampaign(originalCampaignId, true);

        dispatch(setLoadedDirectSalesDataWithPublisherDefaults({ commonDeal, lines: updatedLines }));

        const polygonsResult = await getPolygons(marketId, originalCampaignId, cancelFunctions);
        dispatch(loadPolygons(transformPolygonsResponse(polygonsResult)));

        if (commonDeal.dealId) {
          await handlePricedSolution({
            campaignId: commonDeal.dealId,
            marketId,
            pricingSolution,
            lines: updatedLines,
          });
        }

        dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
        dispatch(changeDealData({ dealId: originalCampaignId }));
      } catch (error) {
        dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
      }
    },
    [marketId, campaignId, updatedGridNegotiationTypes, cancelTransientState],
  );

  const handleUpdateGridNegotiationTypes = useCallback((): void => {
    const dispatchNotification = (
      info: { header: string; description?: string; type: ToasterType; timeout?: ToasterTimeout },
      unchangedKeys?: string[],
    ): void => {
      dispatch(addToastNotification(info));

      if (unchangedKeys?.length) {
        dispatch(changeDirectSalesNegotiationFailedValues(unchangedKeys));
      }
    };

    updateGridNegotiationTypes(
      dispatchNotification,
      updatedGridNegotiationTypes,
      campaignId,
      marketId,
      cancelFunctions,
    );
  }, [updatedGridNegotiationTypes, campaignId, marketId]);

  const loadFailedCloneCampaignFromPollResponse = useCallback(
    (campaignResponse: DirectSalesCampaignResponse): ReturnType<typeof transformDirectSalesBookingResponse> => {
      const linesAtConfirm = linesRef.current;
      const transformedCampaign = transformDirectSalesBookingResponse(campaignResponse);
      const previousAvailabilityByLineId = new Map(linesAtConfirm.map((line) => [line.lineId, line.availability]));
      const mergedLines = transformedCampaign.lines.map((line) => ({
        ...line,
        availability: (line.lineId && previousAvailabilityByLineId.get(line.lineId)) || line.availability,
      }));

      dispatch(updateDirectSalesSessionLock(transformedCampaign.sessionLockState));
      dispatch(
        setLoadedDirectSalesDataWithPublisherDefaults({
          commonDeal: transformedCampaign.deal,
          lines: mergedLines,
        }),
      );

      return { ...transformedCampaign, lines: mergedLines };
    },
    [linesRef],
  );

  const handleFailedSolveOrFailedPriceLine = useCallback((dealLines: DirectSalesCampaignDealLineResponse[]): void => {
    const failedLines = getDirectSalesFailedLines(dealLines);

    dispatch(addNotifications(transformFailedLinesToConfirmToasts(failedLines)));

    failedLines.forEach(({ id: lineId }) => {
      if (lineId) dispatch(clearLineAssets(lineId));
    });
  }, []);

  const confirmChanges = useCallback(async (): Promise<void> => {
    dispatch(changeFormParams({ isEditingDisabled: true, isLoading: true }));

    if (updatedGridNegotiationTypes.length) {
      handleUpdateGridNegotiationTypes();
    }

    try {
      await acceptOrDiscardChanges(
        {
          transientAction: DirectSalesTransientStates.ACCEPT,
        },
        marketId,
        campaignId,
        cancelFunctions,
      );

      if (!originalCampaignId) return;

      const pollCompleteHandler = async (campaignResponse: DirectSalesCampaignResponse): Promise<void> => {
        const { deal, lines: updatedLines, sessionLockState } = transformDirectSalesBookingResponse(campaignResponse);

        dispatch(updateDirectSalesSessionLock(sessionLockState));
        dispatch(setLoadedDirectSalesDataWithPublisherDefaults({ commonDeal: deal, lines: updatedLines }));

        if (deal.dealId) {
          await handlePricedSolution({
            campaignId: deal.dealId,
            marketId,
            pricingSolution: campaignResponse.campaign.body.pricingSolution,
            lines: updatedLines,
          });
        }

        dispatch(changeDealData({ dealId: originalCampaignId }));
        dispatch(clearTransientLineStates());
        dispatch(hideBanner());
      };

      const poll = pollDealSalesCampaign(marketId, originalCampaignId, cancelFunctions);

      poll(({ data }: { data: DirectSalesCampaignResponse }): boolean => {
        const {
          id,
          campaign: {
            body: {
              campaignDefinition: { dealLines },
            },
          },
        } = data;

        const isFailedSolveOrFailedPriceLine = isSomeDirectSalesStatusFailed(dealLines);
        const isClonedCampaign = isDirectSalesCampaignCloned(id);

        if (isClonedCampaign && !isFailedSolveOrFailedPriceLine) return true;

        if (isClonedCampaign) {
          loadFailedCloneCampaignFromPollResponse(data);
        } else {
          pollCompleteHandler(data);
        }

        if (isFailedSolveOrFailedPriceLine) {
          handleFailedSolveOrFailedPriceLine(dealLines);
        }

        dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
        return false;
      }, 1000);
    } catch (error) {
      dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
    }
  }, [marketId, campaignId, updatedGridNegotiationTypes]);

  const commitTransientDeal = useCallback(async (): Promise<void> => {
    if (!isDirectSalesCampaignCloned(campaignId)) return;

    if (!transientStateDealLines || isEmpty(transientStateDealLines)) return;

    const confirmChangesValidationResult: ValidationResult = validateAttemptToConfirmChanges({
      transientStateDealLines,
      lines,
    });

    if (!confirmChangesValidationResult.isValid && confirmChangesValidationResult.errorStatus) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      showBannerInformation(confirmChangesValidationResult.errorStatus);
    } else {
      confirmChanges();
    }
  }, [
    campaignId,
    isEqual(previousTransientLines, transientStateDealLines),
    isEqual(previousLines, lines),
    confirmChanges,
  ]);

  const showBannerInformation = useCallback(
    (
      bannerInformation: CampaignVersioningErrors | CampaignVersioningEditingStatus,
      providedCommitTransientDeal?: (campaignId: string) => Promise<void>,
    ): void => {
      const bannerDisplayInformation = getCampaignVersioningMessageExtended(
        navigate,
        discardChanges,
        providedCommitTransientDeal ?? commitTransientDeal,
        async () => {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          await editDeal();
        },
        isCommitDisabled,
        isCampaignCloned,
      )[bannerInformation];

      dispatch(showBanner(bannerDisplayInformation));
    },
    [
      navigate,
      discardChanges,
      commitTransientDeal,
      isCommitDisabled,
      isEqual(transientStateDealLines, previousTransientLines),
    ],
  );

  const addNewLine = async ({ backupFormDataLines }: { backupFormDataLines: DirectSalesLine[] }): Promise<void> => {
    const backupLineNames = backupFormDataLines.map((line) => line.name.toLowerCase());
    const newLine = createNewDirectSalesLine({
      backupLineNames,
      defaultNonNetworkPricingMode: defaultNonNetworkPricingModeForNewLine,
    });

    const formData = transformDirectSalesDealLineRequest(newLine);

    dispatch(changeFormParams({ isEditingDisabled: true, isLoading: true }));
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID?.();
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY?.();
    dispatch(setIsFetchingAdvancedAvailabilityReport(false));

    try {
      const { id } = await createDirectSalesLine(formData, marketId, campaignId, cancelFunctions);

      const {
        campaign: {
          body: {
            campaignDefinition: { dealLines: savedDealLines },
          },
        },
        deal: commonDeal,
        lines: updatedLines,
      } = await getUpdatedDirectSalesCampaign();

      if (campaignId !== id) {
        dispatch(changeDealData({ dealId: id }));

        if (isDirectSalesCampaignCloned(id)) {
          showBannerInformation(CampaignVersioningEditingStatus.IN_PROGRESS);
        }
      }

      const savedLine = savedDealLines.find(({ name }) => name === newLine.name);

      if (savedLine && savedLine.id) {
        dispatch(setLoadedDirectSalesDataWithPublisherDefaults({ commonDeal, lines: updatedLines }));
        dispatch(changeActiveDirectSalesLine(savedLine.id));
      }
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
    }
  };

  const editDeal = async ({
    currentLineData,
    directSalesData,
    campaignDefinitionData,
  }: {
    currentLineData?: Partial<Line>;
    directSalesData?: Partial<DirectSales>;
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  } = {}): Promise<boolean> => {
    const dealLines = mergeCurrentLineToAllLines(lines as DirectSalesLine[], {
      ...currentLine,
      ...directSales,
      ...currentLineData,
      ...directSalesData,
    });

    const payload = {
      deal: {
        name: dealName,
        advertiser: advertiser as Advertiser,
        brand: brand as Brand,
        productCategory: productCategory as CodeNameModel,
        agency,
        specialist,
        salesPerson,
        salesTeam,
        adminPerson,
        productCategoryExclusivity: campaignDefinitionData?.productCategoryExclusivity || productCategoryExclusivity,
        duplicationExclusion: campaignDefinitionData?.duplicationExclusion || duplicationExclusion,
        juxtapositionExclusion: campaignDefinitionData?.juxtapositionExclusion || juxtapositionExclusion,
        exclusions: campaignDefinitionData?.exclusions || exclusions,
        advertiserBlockExclusion: campaignDefinitionData?.advertiserBlockExclusion || advertiserBlockExclusion,
        agencyBlockExclusion: campaignDefinitionData?.agencyBlockExclusion || agencyBlockExclusion,
        specialistBlockExclusion: campaignDefinitionData?.specialistBlockExclusion || specialistBlockExclusion,
      },
      lines: transformAllLineData({ lines: dealLines, startDateOffset }),
    };

    const formData = transformDirectSalesWithLineFormRequest(payload);

    dispatch(changeFormParams({ isEditingDisabled: true }));
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID?.();
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY?.();
    dispatch(setIsFetchingAdvancedAvailabilityReport(false));

    try {
      const { id } = await editDirectSalesCampaign(formData, marketId, campaignId, cancelFunctions);

      const {
        campaign: {
          header,
          body: {
            campaignDefinition: { dealLines: savedDealLines },
            allocationSolution,
          },
        },
      } = await getUpdatedDirectSalesCampaign();

      if (campaignId !== id) {
        dispatch(changeDealData({ dealId: id }));

        if (isDirectSalesCampaignCloned(id)) {
          showBannerInformation(CampaignVersioningEditingStatus.IN_PROGRESS);
        }
      }

      dispatch(
        changeDealData({
          salesPerson: header.salesPerson || null,
          salesTeam: header.salesTeam || null,
          adminPerson: header.adminPerson || null,
        }),
      );

      const hasActiveCampaignPlanLink =
        Boolean(directSales.campaignPlanShareUrl) && directSales.enabledForSharing !== false;

      if (hasCampaignPlanLinkInvalidatingChange && hasActiveCampaignPlanLink && header.enabledForSharing === false) {
        dispatch(changeDirectSalesData({ enabledForSharing: false }));
        dispatch(
          addToastNotification({
            header: CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_MESSAGE,
            description: CAMPAIGN_PLAN_LINK_DISABLED_ON_SAVE_DESCRIPTION,
            timeout: 5000,
            type: 'Info',
          }),
        );
      }

      dispatch(
        updateBackupFormDataCommonDeal({
          dealName,
          advertiser,
          brand,
          productCategory,
          agency,
          specialist,
          salesPerson: header.salesPerson || null,
          salesTeam: header.salesTeam || null,
          adminPerson: header.adminPerson || null,
          productCategoryExclusivity: campaignDefinitionData?.productCategoryExclusivity || productCategoryExclusivity,
          duplicationExclusion: campaignDefinitionData?.duplicationExclusion || duplicationExclusion,
          juxtapositionExclusion: campaignDefinitionData?.juxtapositionExclusion || juxtapositionExclusion,
          exclusions: campaignDefinitionData?.exclusions || exclusions,
          advertiserBlockExclusion: campaignDefinitionData?.advertiserBlockExclusion || advertiserBlockExclusion,
          agencyBlockExclusion: campaignDefinitionData?.agencyBlockExclusion || agencyBlockExclusion,
          specialistBlockExclusion: campaignDefinitionData?.specialistBlockExclusion || specialistBlockExclusion,
        }),
      );

      const savedLine = savedDealLines.find(({ name }) => name === currentLine.name || name === currentLineData?.name);

      if (savedLine?.id && !currentLine.lineId) {
        dispatch(addDirectSalesLineId(savedLine.id));
      } else if (savedLine?.id && currentLine.lineId) {
        const isDirectSalesChange = !isEmpty(directSalesData);
        const isCurrentLineChange = !isEmpty(currentLineData);

        if (isDirectSalesChange) dispatch(changeDirectSalesData(directSalesData));

        if (isCurrentLineChange) dispatch(changeDealCurrentLineData(currentLineData));

        if (isDirectSalesChange || isCurrentLineChange) {
          dispatch(
            updateBackupFormDataLine({
              lineId: currentLine.lineId,
              newBackupLineData: { ...currentLineData, ...directSalesData },
            }),
          );
          dispatch(clearDirectSalesPrices());
          dispatch(clearDirectSalesAssetList());
          dispatch(hidePlannerSecondaryPanel());
        }
      }

      if (savedDealLines.length) {
        dispatch(setDirectSalesStates(buildLinesStatesWithAllocation(savedDealLines, allocationSolution, id)));
      }

      dispatch(setCampaignViolations([]));

      return true;
    } catch (error) {
      const campaignViolations = getCampaignViolations(error);

      if (!campaignViolations.length) {
        dispatch(setCampaignViolations([]));
        handleDraftFail();

        return false;
      }

      dispatch(resetCampaignLevelChanges());

      if (isCampaignDrawerOpenRef.current) {
        dispatch(setCampaignViolations(campaignViolations));
      } else {
        dispatch(
          addToastNotification({
            header: CAMPAIGN_VIOLATION_ERROR_HEADER,
            description: [...new Set(campaignViolations.map(({ message }) => message))].join(' '),
            timeout: 10000,
            type: 'Failure',
          }),
        );
      }

      return false;
    } finally {
      dispatch(changeFormParams({ isEditingDisabled: false }));
    }
  };

  const createCampaign = async ({
    campaignDefinitionData,
    linesData,
  }: {
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>;
    linesData?: Partial<DirectSalesLine>[];
    infoMessage?: string;
    errorMessage?: string;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }): Promise<boolean> => {
    const defaultCampaignName = `${advertiser?.name}_${brand?.name}${salesPerson ? `_${salesPerson.name}` : ''}`;

    const payload = {
      deal: {
        name: campaignDefinitionData?.name || dealName || defaultCampaignName,
        advertiser: campaignDefinitionData?.advertiserId
          ? { code: campaignDefinitionData.advertiserId, name: '' }
          : (advertiser as Advertiser),
        brand: campaignDefinitionData?.brandId ? { code: campaignDefinitionData.brandId, name: '' } : (brand as Brand),
        productCategory: campaignDefinitionData?.productCategory
          ? { code: campaignDefinitionData.productCategory, name: '' }
          : (productCategory as CodeNameModel),
        agency: campaignDefinitionData?.agencyId ? { code: campaignDefinitionData?.agencyId, name: '' } : agency,
        specialist: campaignDefinitionData?.specialistId
          ? { code: campaignDefinitionData?.specialistId, name: '' }
          : specialist,
        salesPerson: campaignDefinitionData?.salesPerson || salesPerson,
        salesTeam: campaignDefinitionData?.salesTeam || salesTeam,
        adminPerson: campaignDefinitionData?.adminPerson || adminPerson,
        productCategoryExclusivity: campaignDefinitionData?.productCategoryExclusivity || productCategoryExclusivity,
        duplicationExclusion: campaignDefinitionData?.duplicationExclusion || duplicationExclusion,
        juxtapositionExclusion: campaignDefinitionData?.juxtapositionExclusion || juxtapositionExclusion,
        exclusions: campaignDefinitionData?.exclusions || exclusions,
        advertiserBlockExclusion: campaignDefinitionData?.advertiserBlockExclusion || advertiserBlockExclusion,
        agencyBlockExclusion: campaignDefinitionData?.agencyBlockExclusion || agencyBlockExclusion,
        specialistBlockExclusion: campaignDefinitionData?.specialistBlockExclusion || specialistBlockExclusion,
      },
      lines: transformAllLineData({
        lines: linesData || lines,
        startDateOffset,
      }),
    };

    const formData = transformDirectSalesWithLineFormRequest(payload);

    dispatch(changeFormParams({ isEditingDisabled: true, isLoading: true }));

    try {
      const { id } = await createDirectSalesCampaign(formData, marketId, cancelFunctions);

      if (id) {
        navigate(`/planner/direct-sales/${id}`);
        dispatch(clearCampaignPlanShareUrl());
      }

      return true;
    } catch {
      return false;
    } finally {
      dispatch(changeFormParams({ isEditingDisabled: false, isLoading: false }));
    }
  };

  const submitNewDeal = async ({
    campaignDefinitionData,
  }: {
    campaignDefinitionData?: Partial<DirectSalesCampaignDefinitionExclusions>;
  } = {}): Promise<boolean> => {
    return createCampaign({
      campaignDefinitionData,
      linesData: [{ name: generateUniqueDealLineName([], 1), ...directSales }],
    });
  };

  const duplicateCampaign = async ({
    campaignStartDate,
    campaignDefinitionData,
    linesData = lines,
    areCancelledLinesIncluded,
  }: {
    campaignStartDate: Date;
    areCancelledLinesIncluded: boolean;
    campaignDefinitionData: Partial<DirectSalesCampaignDefinitionExclusions>;
    linesData: Partial<DirectSalesLine>[];
  }): Promise<void> => {
    const linesToDuplicate = areCancelledLinesIncluded
      ? linesData
      : linesData.filter((line: DirectSalesLine) => line.state.status !== DirectSalesStatusOption.CANCELLED);
    const linesWithUpdatedDates = updateDatesForDirectSalesLines(linesToDuplicate, campaignStartDate);

    await createCampaign({
      campaignDefinitionData,
      linesData: linesWithUpdatedDates,
    });
  };

  const saveDeal = async (
    campaignDefinitionData?: DirectSalesCampaignDefinitionExclusions,
    callback?: VoidFunction,
  ): Promise<boolean> => {
    let success: boolean;

    if (!campaignId) {
      success = await submitNewDeal({ campaignDefinitionData });
    } else {
      success = await editDeal({ campaignDefinitionData });
    }

    if (success) {
      dispatch(
        addToastNotification({
          description: 'Campaign saved successfully',
          timeout: 5000,
          type: 'Success',
        }),
      );

      callback?.();
    }

    return success;
  };

  const saveLinePolygon = useCallback(
    async (polygonId: string, polygonData: PolygonRequest): Promise<void> => {
      const cloneCampaignId = !isCampaignCloned || campaignId.includes('clone') ? campaignId : `clone-${campaignId}`;

      try {
        await addLinePolygon(
          { marketId, campaignId: cloneCampaignId, lineId: dealLineId, polygonId, data: polygonData },
          cancelFunctions,
        );

        if (!isCampaignCloned && isPricedCampaign) {
          dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: true }));

          const {
            campaign: {
              header: { id },
            },
          } = await getUpdatedDirectSalesCampaign();

          if (cloneCampaignId !== id) {
            dispatch(changeDealData({ dealId: id }));

            if (isDirectSalesCampaignCloned(id)) {
              showBannerInformation(CampaignVersioningEditingStatus.IN_PROGRESS);
            }
          }

          dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: false }));
        }
      } catch {
        dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: false }));
      }
    },
    [isCampaignCloned, isPricedCampaign, campaignId, dealLineId],
  );

  const removeLinePolygon = useCallback(
    async (polygonId: string): Promise<void> => {
      const cloneCampaignId = !isCampaignCloned || campaignId.includes('clone') ? campaignId : `clone-${campaignId}`;

      try {
        await deleteLinePolygon(
          { marketId, campaignId: cloneCampaignId, lineId: dealLineId, polygonId },
          cancelFunctions,
        );
        dispatch(removeLassoSelection({ lineId: dealLineId, polygonId }));

        if (!isCampaignCloned && isPricedCampaign) {
          dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: true }));

          const {
            campaign: {
              header: { id },
            },
          } = await getUpdatedDirectSalesCampaign();

          if (cloneCampaignId !== id) {
            dispatch(changeDealData({ dealId: id }));

            if (isDirectSalesCampaignCloned(id)) {
              showBannerInformation(CampaignVersioningEditingStatus.IN_PROGRESS);
            }
          }

          dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: false }));
        }
      } catch {
        dispatch(updateCurrentLineAndBackupLineProperty({ lineId: dealLineId, type: 'isSaving', value: false }));
      }
    },
    [isCampaignCloned, isPricedCampaign, campaignId, dealLineId],
  );

  const handleTransientBanner = useCallback(
    (newTransientState: CampaignVersioningEditingStatus | CampaignVersioningErrors | undefined) => {
      if (!newTransientState && !!currentTransientState) {
        dispatch(changeDealData({ dealId: originalCampaignId }));
        dispatch(clearTransientLineStates());
        dispatch(hideBanner());
      }

      if (isDirectSalesSessionLockedAndReadOnly || !newTransientState) return;

      dispatch(changeTransientState(newTransientState));
      showBannerInformation(newTransientState, commitTransientDeal);
    },
    [
      currentTransientState,
      originalCampaignId,
      isDirectSalesSessionLockedAndReadOnly,
      showBannerInformation,
      commitTransientDeal,
    ],
  );

  return {
    editDeal,
    saveDeal,
    saveLinePolygon,
    removeLinePolygon,
    duplicateCampaign,
    confirmChanges,
    addNewLine,
    handleTransientBanner,
    cancelTransientState,
    discardChanges,
  };
};

export default useUpdateDirectSalesCampaign;
